"use client";

import { useEffect, useMemo } from "react";
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
} from "@tanstack/react-query";
import type { RealtimeChannel } from "@supabase/supabase-js";

import {
  getNotificationById,
  getNotificationPreferences,
  getUnreadNotificationCount,
  listNotifications,
  markAllNotificationsRead,
  markNotificationRead,
  updateNotificationPreferences,
} from "@/lib/notifications/api";
import {
  NOTIFICATION_PAGE_SIZE,
  NOTIFICATION_PREVIEW_SIZE,
  type AppNotification,
  type NotificationListFilter,
  type NotificationPreferences,
} from "@/lib/notifications/types";
import { queryKeys } from "@/lib/query-keys";
import { getSupabaseBrowserClient } from "@/lib/supabase/client";

function flattenNotifications(
  data: InfiniteData<AppNotification[]> | undefined,
) {
  return data?.pages.flat() ?? [];
}

function upsertNotification(
  items: AppNotification[],
  notification: AppNotification,
  limit?: number,
) {
  const next = [
    notification,
    ...items.filter((item) => item.id !== notification.id),
  ].sort(
    (left, right) =>
      new Date(right.createdAt).getTime() - new Date(left.createdAt).getTime(),
  );

  return typeof limit === "number" ? next.slice(0, limit) : next;
}

export function useNotificationPreview(enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.notificationPreview,
    queryFn: () =>
      listNotifications({
        filter: "all",
        limit: NOTIFICATION_PREVIEW_SIZE,
      }),
    enabled,
  });
}

export function useUnreadNotificationCount(enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.notificationUnreadCount,
    queryFn: getUnreadNotificationCount,
    enabled,
  });
}

export function useNotificationList(
  filter: NotificationListFilter,
  enabled: boolean,
) {
  const query = useInfiniteQuery({
    queryKey: queryKeys.notificationList(filter),
    queryFn: ({ pageParam }) =>
      listNotifications({
        filter,
        before: pageParam,
      }),
    enabled,
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => {
      if (!lastPage || lastPage.length < NOTIFICATION_PAGE_SIZE) {
        return undefined;
      }
      return lastPage[lastPage.length - 1]?.createdAt;
    },
  });

  const notifications = useMemo(
    () => flattenNotifications(query.data),
    [query.data],
  );

  return { ...query, notifications };
}

export function useNotificationPreferences(enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.notificationPreferences,
    queryFn: getNotificationPreferences,
    enabled,
  });
}

export function useMarkNotificationRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: markNotificationRead,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.notifications });
    },
  });
}

export function useMarkAllNotificationsRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: markAllNotificationsRead,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.notifications });
    },
  });
}

export function useUpdateNotificationPreferences() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (patch: Partial<NotificationPreferences>) =>
      updateNotificationPreferences(patch),
    onSuccess: async (preferences) => {
      queryClient.setQueryData(queryKeys.notificationPreferences, preferences);
    },
  });
}

export function useNotificationsRealtime(
  userId: string | null,
  enabled: boolean,
) {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!userId || !enabled) {
      return;
    }

    let cancelled = false;
    let channel: RealtimeChannel | null = null;
    const supabase = getSupabaseBrowserClient();
    const channelName = `notifications:${userId}`;

    async function applyChange(eventType: string, notificationId: string) {
      if (eventType === "DELETE") {
        queryClient.setQueryData<AppNotification[]>(
          queryKeys.notificationPreview,
          (current) => current?.filter((item) => item.id !== notificationId),
        );
      } else {
        const notification = await getNotificationById(notificationId);
        if (cancelled) {
          return;
        }

        if (notification) {
          queryClient.setQueryData<AppNotification[]>(
            queryKeys.notificationPreview,
            (current) =>
              upsertNotification(
                current ?? [],
                notification,
                NOTIFICATION_PREVIEW_SIZE,
              ),
          );
        }
      }

      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: queryKeys.notificationUnreadCount,
        }),
        queryClient.invalidateQueries({
          predicate: (query) =>
            query.queryKey[0] === "notifications" &&
            query.queryKey[1] === "list",
        }),
      ]);
    }

    channel = supabase
      .channel(channelName)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "notifications",
          filter: `recipient_id=eq.${userId}`,
        },
        (payload) => {
          const row = (payload.new ?? payload.old) as { id?: string } | null;
          if (!row?.id) {
            return;
          }
          void applyChange(payload.eventType, row.id);
        },
      )
      .subscribe((status) => {
        if (status === "SUBSCRIBED") {
          void queryClient.invalidateQueries({
            queryKey: queryKeys.notifications,
          });
          return;
        }
        if (status === "CHANNEL_ERROR" || status === "TIMED_OUT") {
          void queryClient.invalidateQueries({
            queryKey: queryKeys.notifications,
          });
        }
      });

    return () => {
      cancelled = true;
      if (channel) {
        void supabase.removeChannel(channel);
      }
    };
  }, [enabled, queryClient, userId]);
}
